// This browser's device identity on the Devices page (docs/07 linked browsers): linked, link it,
// or waiting for approval with the fingerprint to compare (design/isomorphic/src/pages/device-cards.tsx).
import { Browser, Fingerprint as FingerprintIcon, HourglassMedium, SealCheck } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "rizzui";

import type { Device } from "../api/client";
import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { useRelayStatus } from "../relay/useRelayStatus";
import { WidgetCard } from "../ui/Card";
import { Loading } from "../ui/states";
import { Pill } from "../ui/status";
import { useDevice } from "./context";
import { FingerprintChips } from "./FingerprintChips";
import { deviceFingerprint } from "./fingerprint";
import { AUTH_ALG } from "./keys";
import { LinkBrowserForm } from "./LinkBrowserForm";

export function ThisBrowserCard({ devices, className }: { devices: Device[] | undefined; className?: string }) {
  const t = useT().devices;
  const device = useDevice();
  const queryClient = useQueryClient();
  const relayStatus = useRelayStatus(device.kind === "linked" ? device.relay : undefined);

  async function link(name: string, bootstrapCode?: string) {
    await device.link(name, bootstrapCode);
    await queryClient.invalidateQueries({ queryKey: ["devices"] });
  }

  async function onUnlink() {
    await device.unlink();
    toast(t.browser.forgotten);
  }

  if (device.kind === "loading" || devices === undefined) {
    return (
      <WidgetCard className={className}>
        <Loading label={t.browser.checking} />
      </WidgetCard>
    );
  }

  if (device.kind === "linked") {
    const own = deviceFingerprint(device.proto, AUTH_ALG, device.device.keys.authPublicKey);
    return (
      <WidgetCard className={className} label={t.browser.label}>
        <div className="grid grid-cols-[auto_1fr] items-start gap-4 sm:grid-cols-[auto_1fr_auto]">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-lighter text-green-dark">
            <Browser weight="duotone" className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base">{t.browser.linked}</h3>
              <Pill tone="green" icon={<SealCheck weight="fill" />}>
                {t.browser.active}
              </Pill>
            </div>
            <p className="mb-0 mt-1 text-[13px] text-gray-500">
              {t.browser.linkedDetail(device.device.name, <span data-testid="relay-status">{t.relay[relayStatus]}</span>)}
            </p>
            <FingerprintChips value={own} className="mt-3" />
            <p className="mb-0 mt-3 text-xs text-gray-500">
              {t.browser.deviceNote(<span className="font-mono">{device.device.deviceId}</span>)}
            </p>
          </div>
          <Button variant="outline" size="sm" className="col-span-2 justify-self-start sm:col-span-1" onClick={() => void onUnlink()}>
            {t.browser.forget}
          </Button>
        </div>
      </WidgetCard>
    );
  }

  if (device.pending) {
    return (
      <WidgetCard className={className} label={t.browser.label}>
        <div className="flex flex-col items-center py-4 text-center">
          <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-lighter text-orange-dark">
            <HourglassMedium weight="duotone" className="h-8 w-8" />
            <span className="absolute -end-1 -top-1 h-4 w-4 animate-pulse rounded-full bg-orange ring-4 ring-gray-0 dark:ring-gray-50" />
          </span>
          <h3 className="mt-4 text-xl">{t.browser.waiting}</h3>
          <p className="mb-0 mt-1.5 max-w-md text-[13.5px] text-gray-500">
            {t.browser.waitingNote(<span className="font-semibold text-gray-800">{device.pending.device.name}</span>)}
          </p>
          <FingerprintChips value={device.pending.fingerprint} size="lg" testId="device-fingerprint" className="mt-5" />
          <p className="mb-0 mt-5 flex items-center gap-1.5 text-xs text-gray-500">
            <FingerprintIcon weight="duotone" className="h-4 w-4 shrink-0" /> {t.browser.differ}
          </p>
          <p className="mb-0 mt-1 font-mono text-[11.5px] text-gray-500">{device.pending.device.deviceId}</p>
          <Button variant="outline" className="mt-5" onClick={() => void onUnlink()}>
            {t.browser.forget}
          </Button>
        </div>
      </WidgetCard>
    );
  }

  return (
    <LinkBrowserForm className={className} hasActiveDevice={devices.some((d) => d.status === "active")} error={device.error} link={link} />
  );
}
